import React, { useState } from 'react';
import { useBuildComponentTypes } from '../../../../../contexts/BuildComponentTypesContext';
import { maintenanceTemplateApi } from '../../../../../api/maintenanceTemplateApi';
import TemplateEditModal from '../../../TemplatesEditor/TemplateEditModal';
import { EditForm, MaintenanceTemplate } from '../../../TemplatesEditor/types';

interface EditActivityTemplateModalProps {
    template: MaintenanceTemplate;
    /** The row's components (the selected ones in Massredigering): asked afterwards when they kept own values */
    componentCount: number;
    buildingKey: string;
    /** Called with the template as saved, after the component types context has reloaded */
    onSaved: (updated: MaintenanceTemplate) => void;
    onClose: () => void;
}

/**
 * Modal for editing a maintenance activity's template from the draft.
 * Warning: Changes affect ALL components using this template.
 *
 * Requires admin privileges.
 */
export const EditActivityTemplateModal: React.FC<EditActivityTemplateModalProps> = ({
    template,
    componentCount,
    buildingKey,
    onSaved,
    onClose
}) => {
    const { refresh, getTranslation, getBaseType } = useBuildComponentTypes();
    const [saving, setSaving] = useState(false);

    const handleSave = async (form: EditForm): Promise<void> => {
        setSaving(true);
        try {
            const formWithType: EditForm = {
                ...form,
                primaryComponentType: form.primaryComponentType || template.primaryComponentType
            };
            await maintenanceTemplateApi.updateTemplate(template.id, formWithType, { source: 'draft', buildingKey });

            // Refresh the component types context; the plan follows the context
            await refresh();

            const { templateKey: _templateKey, ...saved } = formWithType;
            onSaved({ ...saved, id: template.id });
        } finally {
            setSaving(false);
        }
    };

    return (
        <TemplateEditModal
            show
            template={template}
            componentType={template.primaryComponentType}
            componentTypeKey={template.primaryComponentType}
            componentTypeName={getTranslation(template.primaryComponentType)}
            isNewTemplate={false}
            buildingKey={buildingKey}
            onSave={handleSave}
            onCancel={onClose}
            saving={saving}
            infoText={`Ändringar i mallen påverkar alla komponenter som använder den. Komponenter med anpassat intervall, anpassad schablon eller kostnad enligt offert behåller sina anpassningar; för ${componentCount === 1 ? 'den här komponenten' : `de ${componentCount} valda komponenterna`} får du frågan om de ska följa mallen.`}
            baseType={getBaseType(template.primaryComponentType)}
        />
    );
};
